import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {createRefetchContainer, graphql} from 'react-relay';
import {BaseLevelWrapper} from './BaseLevelWrapper';
import {GroupRow} from '../rows/GroupRow';
import {GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR, GroupProgressCategories} from '../../../../../constants';
import {useGenericReportContext} from '../../../saved-report/GenericReportContext';

const SprintRelayWrapperV2 = ({
	relay,
	tableHeader,
	groupings,
	searchQuery,
	viewer,
	rowIndex,
	enabledColumns,
	nextLevelProps,
	currency,
	customFieldColumnCount,
	customFieldKeys,
	pageSize,
}) => {
	const {formatMessage} = useIntl();
	const {reportId, sharedId} = useGenericReportContext();

	const sprints = viewer.reportData.sprints.edges?.filter(sprint => sprint.node.taskTotals.entities > 0);
	const hasNextPage = viewer.reportData.sprints.pageInfo.hasNextPage && sprints.length >= pageSize;
	const [modalTasksFetching, setModalTasksFetching] = useState(false);

	const sprintGroupingFilter = sprintId => {
		return {
			field: GLOBAL_FILTER_FIELD.PROJECT_SPRINT,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: [sprintId],
		};
	};

	const noSprintGroupingFilter = () => {
		return {
			field: GLOBAL_FILTER_FIELD.SPRINT_CATEGORY,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: GroupProgressCategories.NONE,
		};
	};

	const dataRows = sprints.map(sprint => {
		return {
			...sprint.node,
			generatedName: sprint.node.noSprint ? formatMessage({id: 'common.no_sprints'}) : sprint.node.name,
			currency,
		};
	});

	const data = {
		rows: dataRows,
		groupingFilter: rowData => (rowData.noSprint ? noSprintGroupingFilter() : sprintGroupingFilter(rowData.sprintId)),
	};

	const fetchMore = () => {
		if (!modalTasksFetching) {
			const refetchObject = {};
			setModalTasksFetching(true);
			refetchObject.pageSize = sprints.length + pageSize;
			relay.refetch(refetchObject, null, () => setModalTasksFetching(false));
		}
	};

	const sprintRows = () => {
		return GroupRow(
			data,
			rowIndex,
			searchQuery,
			groupings,
			enabledColumns,
			hasNextPage,
			fetchMore,
			nextLevelProps,
			currency,
			reportId,
			sharedId,
			customFieldColumnCount,
			customFieldKeys
		);
	};

	return rowIndex === 1 ? (
		<BaseLevelWrapper
			tableHeader={tableHeader}
			enabledColumns={enabledColumns}
			getRows={sprintRows}
			currentLength={sprints.length}
			hasNextPage={hasNextPage}
			refetch={relay.refetch}
			currency={currency}
			pageSize={pageSize}
		/>
	) : (
		sprintRows()
	);
};

const SprintRelayWrapperV2Query = graphql`
	query SprintRelayWrapperV2Query_Query(
		$searchQuery: TaskSearchQueryType
		$pageSize: Int
		$reportId: String
		$sharedId: String
	) {
		viewer {
			actualPersonId
			component(name: "sprint_relay_wrapper_v2")
			...SprintRelayWrapperV2_viewer
				@arguments(searchQuery: $searchQuery, pageSize: $pageSize, reportId: $reportId, sharedId: $sharedId)
		}
	}
`;
export {SprintRelayWrapperV2Query};

export default createRefetchContainer(
	SprintRelayWrapperV2,
	{
		viewer: graphql`
			fragment SprintRelayWrapperV2_viewer on Viewer
			@argumentDefinitions(
				searchQuery: {type: "TaskSearchQueryType"}
				pageSize: {type: "Int"}
				reportId: {type: "String"}
				sharedId: {type: "String"}
			) {
				reportData(id: $reportId, sharedId: $sharedId) {
					sprints(first: $pageSize, searchQuery: $searchQuery)
						@connection(key: "TaskReport_sprints", filters: ["searchQuery"]) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								id
								sprintId
								name
								taskTotals {
									work {
										estimate {
											minutes
											points
										}
										remaining {
											minutes
											points
										}
									}
									reported {
										time
										points
										projected
									}
									entities
								}
								noSprint
							}
						}
					}
				}
			}
		`,
	},
	graphql`
		query SprintRelayWrapperV2RefetchQuery(
			$pageSize: Int
			$searchQuery: TaskSearchQueryType
			$reportId: String
			$sharedId: String
		) {
			viewer {
				...SprintRelayWrapperV2_viewer
					@arguments(pageSize: $pageSize, searchQuery: $searchQuery, reportId: $reportId, sharedId: $sharedId)
			}
		}
	`
);
